// Loan Simulator – Girona (Catalonia, Spain)
// Borrowing capacity from the household income, for a mixed-rate mortgage (fixed initial
// rate for 10 years, then a review rate). Taxes and expenses of the purchase are financed.

// ---------------------------------------------------------------------------
// Parameters set by the administrator (updated every half-year).
// TODO: load these from the backend once parameter versions are stored there.
// ---------------------------------------------------------------------------
export const GIRONA_LOAN_PARAMS = {
  version: "Parameters: second half of 2026",
  // Mixed product, by LTV band: initial rate (TIN) and margin over Euribor for the review
  ltvBands: [
    { key: "upTo65", initialRate: 0.0455, margin: 0.0129 },
    { key: "upTo80", initialRate: 0.046, margin: 0.0134 },
    { key: "below90", initialRate: 0.0465, margin: 0.0139 },
    { key: "from90", initialRate: 0.047, margin: 0.0144 },
  ],
  // Fixed and variable products (shown by the Monthly Instalment Calculator)
  fixedProduct: { rate: 0.0455, minTermYears: 10 },
  variableProduct: { initialRate: 0.048, initialMonths: 12, reviewRate: 0.05416 },
  euribor: 0.0317,
  secondResidenceSurcharge: 0.001,
  initialFixedYears: 10,
  ownContribution: {
    civilServant: { oneBorrower: 0.15, twoBorrowers: 0.1 },
    permanent: 0.2,
    liberalProfessional: 0.3,
    selfEmployed: 0.4,
    temporary: 0.4,
    capitalIncome: 0.4,
    noIncome: 0.4,
    secondResidence: 0.4,
  },
  maxInstalmentRatio: 0.4,
  warningInstalmentRatio: 0.45,
  itp: 0.11, // property transfer tax, second-hand (Catalonia)
  vatNewBuild: 0.1,
  ajdNewBuild: 0.015, // stamp duty, new build (Catalonia)
  bankCommission: 0.015,
  appraisal: { vat: 0.21, smallFee: 300, smallBelow: 100000, mediumFee: 400, mediumBelow: 500000, rate: 0.00095 },
  notaryFee: { coefficient: 31.741, exponent: 0.349 },
};

// Fixed rules
export const EMPLOYMENT_SITUATIONS = [
  "civilServant",
  "permanent",
  "liberalProfessional",
  "selfEmployed",
  "temporary",
  "capitalIncome",
  "noIncome",
];
export const MIN_TERM_YEARS = 7;
export const MAX_TERM_YEARS = 30;
export const MAX_AGE_AT_END = 75;
export const MIN_AGE = 18;

export const maxTermForAge = (age) => Math.min(MAX_TERM_YEARS, MAX_AGE_AT_END - age);

// Rounding to €100 (guarded against binary noise such as 30000.000000000004)
const clean = (x) => Number((x / 100).toFixed(9));
export const roundDown100 = (x) => Math.floor(clean(x)) * 100;
export const roundUp100 = (x) => Math.ceil(clean(x)) * 100;

const annuityFactor = (annualRate, months) => {
  const r = annualRate / 12;
  return r === 0 ? 1 / months : r / (1 - Math.pow(1 + r, -months));
};

// ---------------------------------------------------------------------------
// Building blocks
// ---------------------------------------------------------------------------
export function appraisalFee(price, params = GIRONA_LOAN_PARAMS) {
  const a = params.appraisal;
  if (price < a.smallBelow) return a.smallFee * (1 + a.vat);
  if (price < a.mediumBelow) return a.mediumFee * (1 + a.vat);
  return a.rate * price * (1 + a.vat);
}

export function notaryFees(price, params = GIRONA_LOAN_PARAMS) {
  return params.notaryFee.coefficient * Math.pow(price, params.notaryFee.exponent);
}

// LTV bands: ≤ 65 %, > 65–80 %, > 80 % and < 90 %, ≥ 90 % (exactly 90 % is the highest band)
export function ltvBandIndex(ltv) {
  if (ltv <= 0.65) return 0;
  if (ltv <= 0.8) return 1;
  if (ltv < 0.9) return 2;
  return 3;
}

function contributionOf(borrower, borrowerCount, params) {
  const c = params.ownContribution[borrower.employment];
  if (borrower.employment === "civilServant") {
    return borrowerCount === 2 ? c.twoBorrowers : c.oneBorrower;
  }
  return c;
}

function purchaseTaxes(propertyType, params) {
  return propertyType === "newBuild"
    ? { rate: params.vatNewBuild + params.ajdNewBuild, vat: params.vatNewBuild, ajd: params.ajdNewBuild }
    : { rate: params.itp };
}

// Step 3 – loan needed for a property price (before and after rounding)
function loanNeeded(price, ctx) {
  const { taxRate, contribution, params } = ctx;
  const savings = roundUp100(contribution * price);
  const exact =
    (price + appraisalFee(price, params) + taxRate * price + notaryFees(price, params) - savings) /
    (1 - params.bankCommission);
  return { savings, exact, loan: roundDown100(exact) };
}

// Step 4 – rates of the mixed product for an LTV band
export function mixedRates(bandIndex, secondResidence, params = GIRONA_LOAN_PARAMS) {
  const band = params.ltvBands[bandIndex];
  const initialRate = band.initialRate + (secondResidence ? params.secondResidenceSurcharge : 0);
  const reviewRate = Math.max(band.margin + params.euribor, initialRate);
  return { bandIndex, initialRate, reviewRate };
}

const ratesFor = (bandIndex, ctx) => mixedRates(bandIndex, ctx.secondResidence, ctx.params);

// Step 5a – largest multiple of €100 whose loan needed stays within the maximum capital
function maximumPrice(maxCapital, ctx) {
  const fits = (steps) => loanNeeded(steps * 100, ctx).exact <= maxCapital;
  if (!fits(1)) return null;
  let lo = 1;
  let hi = 2;
  while (fits(hi)) {
    lo = hi;
    hi *= 2;
  }
  while (hi - lo > 1) {
    const mid = Math.floor((lo + hi) / 2);
    if (fits(mid)) lo = mid;
    else hi = mid;
  }
  return lo * 100;
}

// Repayment schedule with a rate per period: the instalment is calculated in month 1 and
// recalculated on the balance and remaining months whenever a new period starts.
// periods: [{ fromMonth: 1, rate }, { fromMonth: 121, rate }, ...]
export function repaymentSchedule(loan, n, periods) {
  const instalments = [];
  let balance = loan;
  let instalment = 0;
  let rate = periods[0].rate;
  for (let m = 1; m <= n; m++) {
    const period = periods.find((p) => p.fromMonth === m);
    if (period) {
      rate = period.rate;
      instalment = balance * annuityFactor(rate, n - m + 1);
    }
    balance = balance + (balance * rate) / 12 - instalment;
    instalments.push(instalment);
  }
  return instalments;
}

// Periods of the mixed product: initial rate for the fixed period, then the review rate
// (the initial rate for the whole term if the term is not longer than the fixed period)
export function mixedPeriods(rates, n, params = GIRONA_LOAN_PARAMS) {
  const fixedMonths = 12 * params.initialFixedYears;
  return n > fixedMonths
    ? [{ fromMonth: 1, rate: rates.initialRate }, { fromMonth: fixedMonths + 1, rate: rates.reviewRate }]
    : [{ fromMonth: 1, rate: rates.initialRate }];
}

// TAE: monthly rate ρ solving (L − commissions) − Σ a_m / (1 + ρ)^m = 0, by bisection
export function tae(netAmount, instalments) {
  const f = (rho) => {
    let value = netAmount;
    let discount = 1;
    for (const a of instalments) {
      discount /= 1 + rho;
      value -= a * discount;
    }
    return value;
  };
  let lo = -0.5;
  let hi = 0.5;
  while (hi - lo >= 1e-12) {
    const mid = (lo + hi) / 2;
    if (f(mid) < 0) lo = mid;
    else hi = mid;
  }
  return Math.pow(1 + (lo + hi) / 2, 12) - 1;
}

// ---------------------------------------------------------------------------
// Simulation
// ---------------------------------------------------------------------------
export function simulateLoanGirona({
  borrowers,
  oldestAge,
  residence = "main",
  propertyType = "secondHand",
  termYears,
  propertyPrice,
  params = GIRONA_LOAN_PARAMS,
}) {
  const age = Number(oldestAge);
  const T = Number(termYears);
  if (!Number.isInteger(age) || age < MIN_AGE) return { error: "age" };
  if (!Number.isInteger(T) || T < MIN_TERM_YEARS || T > maxTermForAge(age)) return { error: "term" };

  // Step 1 – household income and own contribution
  const incomes = borrowers.map((b) => (Number(b.monthlyIncome) || 0) * Number(b.paymentsPerYear));
  const income = incomes.reduce((a, b) => a + b, 0);
  if (!(income > 0)) return { error: "income" };

  const secondResidence = residence === "second";
  const contribution = secondResidence
    ? params.ownContribution.secondResidence
    : borrowers.reduce((sum, b, h) => sum + incomes[h] * contributionOf(b, borrowers.length, params), 0) /
      income;

  // Step 2 – maximum instalment
  const otherLoans = borrowers.reduce((sum, b) => sum + (Number(b.otherLoans) || 0), 0);
  const maxAnnualInstalment = params.maxInstalmentRatio * income - 12 * otherLoans;
  if (maxAnnualInstalment <= 0) return { error: "noCapacity", income, maxAnnualInstalment };

  const n = 12 * T;
  const taxes = purchaseTaxes(propertyType, params);
  const ctx = { taxRate: taxes.rate, contribution, secondResidence, params };

  let price = Number(propertyPrice);
  const withPrice = price > 0;
  let rates;

  if (withPrice) {
    // Step 5b – the price entered is used directly
    const { loan } = loanNeeded(price, ctx);
    rates = ratesFor(ltvBandIndex(loan / price), ctx);
  } else {
    // Step 5a – iterate on the LTV band, starting from the highest
    const tried = [];
    let band = params.ltvBands.length - 1;
    for (;;) {
      rates = ratesFor(band, ctx);
      const maxCapital = (maxAnnualInstalment / 12) / annuityFactor(rates.initialRate, n);
      price = maximumPrice(maxCapital, ctx);
      if (price === null) return { error: "noCapacity", income, maxAnnualInstalment };
      tried.push(band);
      const resultBand = ltvBandIndex(loanNeeded(price, ctx).loan / price);
      if (resultBand === band) break;
      if (tried.includes(resultBand)) {
        // Alternating between two bands: use the band with the higher rate
        const higher = [band, resultBand].reduce((a, b) =>
          params.ltvBands[b].initialRate > params.ltvBands[a].initialRate ? b : a
        );
        if (higher !== band) {
          band = higher;
          rates = ratesFor(band, ctx);
          price = maximumPrice((maxAnnualInstalment / 12) / annuityFactor(rates.initialRate, n), ctx);
        }
        break;
      }
      band = resultBand;
    }
  }

  const { savings, exact: exactLoan, loan } = loanNeeded(price, ctx);
  const fixedMonths = 12 * params.initialFixedYears;
  const instalments = repaymentSchedule(loan, n, mixedPeriods(rates, n, params));

  // Commissions paid at signing and TAE
  const appraisal = appraisalFee(price, params);
  const commissions = appraisal + params.bankCommission * loan;

  // Instalment-to-income ratio, with the loan before rounding (as in the reference model)
  const ratio = (12 * (exactLoan * annuityFactor(rates.initialRate, n) + otherLoans)) / income;

  return {
    withPrice,
    propertyPrice: price,
    income,
    contribution,
    maxAnnualInstalment,
    savings,
    exactLoan,
    loanAmount: loan,
    ltv: loan / price,
    bandKey: params.ltvBands[rates.bandIndex].key,
    initialRate: rates.initialRate,
    reviewRate: rates.reviewRate,
    termYears: T,
    termMonths: n,
    instalmentInitial: instalments[0],
    instalmentReview: n > fixedMonths ? instalments[fixedMonths] : null,
    tae: tae(loan - commissions, instalments),
    ratio,
    ratioWarning: withPrice && ratio > params.warningInstalmentRatio,
    taxesAndExpenses: loan + savings - price,
    breakdown: {
      appraisal,
      purchaseTax: taxes.rate * price,
      purchaseTaxKind: propertyType === "newBuild" ? "vatAjd" : "itp",
      purchaseTaxRate: taxes.rate,
      notary: notaryFees(price, params),
      commission: params.bankCommission * exactLoan,
    },
    parameterVersion: params.version,
  };
}
